import {t} from 'i18next';
import {Step} from '../../common/arr-stepper/ArrStepper';
import {ISelectItem} from '../../../shared/types';
import {
  DocumentListRequestPayload,
  DocumentListSortConfiguration,
  DocumentListSourceDetail,
  DocumentListSourceInfo,
  DocumentListSessionObjects,
  DocumentViewerListIdRequest,
} from '../../../api/types/document.type';
import {SourceTypeCode} from '../../../shared/constants';

// Branding position mapping for TIFF/PDF components
export const BRANDING_POSITIONS = {
  'left-header': 'leftHeaderBranding',
  'right-header': 'rightHeaderBranding',
  'center-header': 'centerHeaderBranding',
  'left-center-header': 'leftCenterHeaderBranding',
  'right-center-header': 'rightCenterHeaderBranding',
  header: 'headerBranding',
  'left-footer': 'leftFooterBranding',
  'right-footer': 'rightFooterBranding',
  'center-footer': 'centerFooterBranding',
  'left-center-footer': 'leftCenterFooterBranding',
  'right-center-footer': 'rightCenterFooterBranding',
  footer: 'footerBranding',
} as const;

// Shared constants used across multiple components

// Default branding constants (used in multiple components)
export const DEFAULT_FONT_FAMILY = 'Arial';
export const DEFAULT_FONT_STYLE = 'Regular';
export const DEFAULT_FONT_SIZE = '10';

// Form action enum (used across all form components)
export enum FormAction {
  SAVE = 'save',
  NEXT = 'next',
}

// Form mode enum
export enum FormMode {
  CREATE = 'create',
  EDIT = 'edit',
  VIEW = 'view',
}

// Insert field type enum
export enum FieldType {
  METADATA = 'metadata',
  PRODUCTION = 'production',
}

// Document selection method enum
export enum DocumentSelectionMethod {
  FILE_TYPE = 'file-type',
  TAGS = 'tags',
  SEARCHES = 'searches',
  FOLDERS = 'folders',
}

// contextType values expected by the documents-selection save/view APIs
export enum DocumentSelectionApiContextType {
  TAGS = 'TAGS',
  SEARCHES = 'SAVEDSEARCH',
  FOLDERS = 'FOLDERS',
}

// Preserve color option enum
export enum PreserveColorOption {
  SPECIFY_DOCS = 'specify-docs',
  ALL_DOCS = 'all-docs',
}

// Regenerate type enum
export enum RegenerateType {
  RegenerateAll = 0, // Restart from the beginning
  RegenerateOnlyErrors = 1, // Restart from where it left off
}

// Production status enum
export enum ProductionStatus {
  FAILED = 'FAILED',
  INPROGRESS = 'INPROGRESS',
  COMPLETED = 'COMPLETED',
  DRAFT = 'DRAFT',
}

// Numeric productionCurrentStatus code returned by
// GetProductionBasicInfoInEditMode — distinct from the string ProductionStatus enum above.
export enum ProductionCurrentStatusCode {
  INPROGRESS = 1,
  COMPLETED = 2,
}

// Numeric currentStep returned by GetProductionBasicInfoInEditMode — mirrors getStepKeyFromNumber below.
export enum ProductionWizardStepNumber {
  BASIC_INFO = 1,
  PRODUCTION_COMPONENTS = 2,
  NUMBERING_AND_SORTING = 3,
  DOCUMENT_SELECTION = 4,
  PRIV_GUARD = 5,
  DELIVERY_OPTIONS = 6,
  SUMMARY_AND_PREVIEW = 7,
  GENERATE = 8,
  QUALITY_CONTROL_AND_CONFIRMATION = 9,
}

// Production state enum (string values from API)
export enum ProductionState {
  INPROGRESS = 'INPROGRESS',
  PAGEDATACOMPLETED = 'PAGEDATACOMPLETED',
  PAGECOUNTINPROGRESS = 'PAGECOUNTINPROGRESS',
  PAGECOUNTCOMPLETED = 'PAGECOUNTCOMPLETED',
  DATAGENINPROGRESS = 'DATAGENINPROGRESS',
  AFTERDATAGENFAILED = 'AFTERDATAGENFAILED',
  COPYINPROGRESS = 'COPYINPROGRESS',
  ARCHIVEINPROGRESS = 'ARCHIVEINPROGRESS',
  GENERATECOMPLETE = 'GENERATECOMPLETE',
  QCINPROGRESS = 'QCINPROGRESS',
  QCCOMPLETE = 'QCCOMPLETE',
  FAILED = 'FAILED',
  UNCOMMITFAILED = 'UNCOMMITFAILED',
  COMMITFAILED = 'COMMITFAILED',
  UNCOMMITINPROGRESS = 'UNCOMMITINPROGRESS',
  COMMITINPROGRESS = 'COMMITINPROGRESS',
  UNCOMMITED = 'UNCOMMITED',
  COMPLETED = 'COMPLETED',
  CANCELINPROGRESS = 'CANCELINPROGRESS',
  CANCELCOMPLETED = 'CANCELCOMPLETED',
  CANCELFAILED = 'CANCELFAILED',
}

// Production type enum
export enum ProductionType {
  ADD = 0,
  GENERATE = 1,
  REMOVE = 2,
}

// Re-export ToastType from shared constants for convenience
export {ToastType} from '../../../shared/constants';

// Module type enum
export enum ModuleType {
  PRODUCTION = 'PRODUCTION',
  EXPORT = 'EXPORT',
}

// Component type enum
export enum ComponentType {
  TIFF = 'TIFF',
  PDF = 'PDF',
  NATIVE = 'NATIVE',
}

// Tree ID constants for slip sheet groups in ArrTreeItemSelector
export const SlipSheetTreeId = {
  FOLDER_TREE: 'folder-tree',
  TAG_TREE: 'tag-tree',
  REDACTION_TAG_TREE: 'redaction-tag-tree',
  COMMENT_TREE: 'comment-tree',
  ASSIGNMENT_TREE: 'assignment-tree',
  METADATA_TREE: 'metadata-tree',
  CALCULATED_TREE: 'calculated-tree',
} as const;

export const createDocumentListSortConfiguration = (
  overrides?: Partial<DocumentListSortConfiguration>
): DocumentListSortConfiguration => ({
  optimizedCols: '',
  optimizedSelectFieldIDs: '',
  optimizedSelectFieldOrder: '',
  manualCols: '',
  manualSelectFieldIDs: '',
  manualSelectFieldOrder: '',
  selectFields: '',
  configuredCols: '',
  order: '',
  showCompletedDocOptimized: false,
  showCompletedDocManual: false,
  configuredShowCompletedDoc: false,
  selectFieldTemp: '',
  selectFieldDataTypeOptimized: [],
  selectFieldDataType: [],
  selectFieldDataTypeManual: [],
  redirectionType: 0,
  manualOrder: '',
  ...overrides,
});

export interface CreateDocumentListRequestPayloadArgs {
  sourceId: number;
  sourceTypeCode: SourceTypeCode;
  docViewSourceTypeCode?: SourceTypeCode;
  sourceDetailOverrides?: Partial<DocumentListSourceDetail>;
  docsToExcludeOverrides?: Partial<DocumentListSourceDetail>;
  sourceInfoOverrides?: Partial<
    Omit<DocumentListSourceInfo, 'sourceDetails' | 'docsToExclude'>
  >;
  docViewSessionOverrides?: Partial<DocumentListSessionObjects>;
  sortConfigurationOverrides?: Partial<DocumentListSortConfiguration>;
}

const createBaseSourceDetail = (
  overrides?: Partial<DocumentListSourceDetail>
): DocumentListSourceDetail => ({
  sourceID: 0,
  pureHits: false,
  emailThread: false,
  nearDup: false,
  family: false,
  conceptualSimilar: false,
  uniquePureHits: false,
  uniqueFamilyMembers: false,
  s: 0,
  p: false,
  e: false,
  n: false,
  f: false,
  c: false,
  up: false,
  uf: false,
  ingestionExceptionDetails: [],
  ...overrides,
});

export const createDocumentListRequestPayload = (
  args: CreateDocumentListRequestPayloadArgs
): DocumentListRequestPayload => {
  const {
    sourceId,
    sourceTypeCode,
    docViewSourceTypeCode,
    sourceDetailOverrides,
    docsToExcludeOverrides,
    sourceInfoOverrides,
    docViewSessionOverrides,
    sortConfigurationOverrides,
  } = args;

  const sortconfiguration = createDocumentListSortConfiguration(
    sortConfigurationOverrides
  );

  const sourceDetail = createBaseSourceDetail({
    sourceID: sourceId,
    ...sourceDetailOverrides,
  });

  const docsToExclude = createBaseSourceDetail({
    ...docsToExcludeOverrides,
  });

  const baseSourceInfo: DocumentListSourceInfo = {
    userID: 0,
    securityGroupID: 0,
    roleID: 0,
    projectID: 0,
    sourceDetails: [sourceDetail],
    sourceTypeCode,
    assignmentSource: '',
    isMyAssignment: false,
    reviewerID: 0,
    isInverse: false,
    isDefaultLoad: false,
    isAllChildrenSelected: false,
    docsToExclude: [docsToExclude],
    documentListMasterID: 0,
    filters: [],
    sqlUnitParam: [],
    columnFilters: [],
    selectedAssignmentName: '',
    sessionDocViewId: 0,
    destinationTypeCode: '',
    sampleMethod: '',
    uniqueDocumentCount: 0,
    numberToAssign: 0,
    stR_SelectedSearchIds: [],
    destinationContextTypeCode: '',
    isDocumentCount: false,
    totalDocumentToView: 0,
    reportID: '',
    prevPageName: '',
    dataSetStatus: '',
    ...sourceInfoOverrides,
  };

  const baseDocViewSession: DocumentListSessionObjects = {
    sortconfiguration,
    assignmentSortDetails: [],
    documentListMasterID: 0,
    sourceTypeCode: docViewSourceTypeCode ?? sourceTypeCode,
    sourceID: sourceId,
    assignmentSource: '',
    xmlCompletedDocumentEditedCodingForm: '',
    userCodingFormID: 0,
    docsForCodeSame: [],
    docIdsForDocHistory: [],
    backtoSrcDoclistMasterId: 0,
    backtoSrcSourceId: sourceId,
    backtoSrcSourceTypeCode: docViewSourceTypeCode ?? sourceTypeCode,
    ...docViewSessionOverrides,
  };

  return {
    sourceInfo: baseSourceInfo,
    docViewSessionObjects: baseDocViewSession,
  };
};

export interface CreateDocumentViewerListRequestPayloadArgs extends CreateDocumentListRequestPayloadArgs {
  sessionDocMasterIds?: number[];
  sessionDocViewHistoryKey?: number[];
  sortConfigurationDocViewOverrides?: Partial<DocumentListSortConfiguration>;
  sortConfigDocViewOverrides?: Partial<DocumentListSortConfiguration>;
  defaultAssName?: string;
}

export const createDocumentViewerListRequestPayload = (
  args: CreateDocumentViewerListRequestPayloadArgs
): DocumentViewerListIdRequest => {
  const {
    sessionDocMasterIds = [],
    sessionDocViewHistoryKey = [],
    sortConfigurationDocViewOverrides,
    sortConfigDocViewOverrides,
    defaultAssName = '',
    ...rest
  } = args;

  const basePayload = createDocumentListRequestPayload(rest);

  return {
    objSource: basePayload.sourceInfo,
    docViewSessionObject: basePayload.docViewSessionObjects,
    sessionDocMasterIds,
    sessionDocViewHistoryKey,
    sortConfigurationDocView: createDocumentListSortConfiguration(
      sortConfigurationDocViewOverrides
    ),
    defaultAssName,
    sortConfigDocView: createDocumentListSortConfiguration(
      sortConfigDocViewOverrides
    ),
  };
};

// Step keys type for better type safety
export type StepKey =
  | 'basicInfo'
  | 'productionComponents'
  | 'numberingAndSorting'
  | 'documentSelection'
  | 'privGuard'
  | 'deliveryOptions'
  | 'summaryAndPreview'
  | 'generate'
  | 'qualityControlAndConfirmation';

// Step key constants
export const STEP_KEY_BASIC_INFO: StepKey = 'basicInfo';
export const STEP_KEY_PRODUCTION_COMPONENTS: StepKey = 'productionComponents';
export const STEP_KEY_NUMBERING_AND_SORTING: StepKey = 'numberingAndSorting';
export const STEP_KEY_DOCUMENT_SELECTION: StepKey = 'documentSelection';
export const STEP_KEY_PRIV_GUARD: StepKey = 'privGuard';
export const STEP_KEY_DELIVERY_OPTIONS: StepKey = 'deliveryOptions';
export const STEP_KEY_SUMMARY_AND_PREVIEW: StepKey = 'summaryAndPreview';
export const STEP_KEY_GENERATE: StepKey = 'generate';
export const STEP_KEY_QUALITY_CONTROL_AND_CONFIRMATION: StepKey =
  'qualityControlAndConfirmation';

// Step-specific configuration interface
export interface StepConfig {
  showHeaderNav?: boolean;
  showFooterNav?: boolean;
  showSaveButton?: boolean;
  showNextButton?: boolean;
}

export const STEP_CONFIG: Partial<Record<StepKey, StepConfig>> = {
  generate: {
    showFooterNav: false,
    showSaveButton: false,
  },
  qualityControlAndConfirmation: {
    showFooterNav: false,
    showSaveButton: false,
    showNextButton: false,
  },
};

export const getStepConfig = (stepKey: string): StepConfig => {
  const config = STEP_CONFIG[stepKey as StepKey];
  return {
    showHeaderNav: config?.showHeaderNav !== false, // Default to true
    showFooterNav: config?.showFooterNav !== false, // Default to true
    showSaveButton: config?.showSaveButton !== false, // Default to true
    showNextButton: config?.showNextButton !== false, // Default to true
  };
};

export const stepperItems = (isProductionSet: boolean = true) => {
  return [
    {
      key: STEP_KEY_BASIC_INFO,
      label: t(
        'productionsAndExports.createNewProduction.stepper.basicInfo.label'
      ),
      tooltipTitle: t(
        'productionsAndExports.createNewProduction.stepper.basicInfo.tooltipTitle'
      ),
      tooltip: t(
        'productionsAndExports.createNewProduction.stepper.basicInfo.tooltip'
      ),
      state: 'current',
    },
    {
      key: STEP_KEY_PRODUCTION_COMPONENTS,
      label: isProductionSet
        ? t(
            'productionsAndExports.createNewProduction.stepper.productionComponents.label'
          )
        : t(
            'productionsAndExports.createNewProduction.stepper.components.label'
          ),
      tooltipTitle: isProductionSet
        ? t(
            'productionsAndExports.createNewProduction.stepper.productionComponents.tooltipTitle'
          )
        : t(
            'productionsAndExports.createNewProduction.stepper.components.tooltipTitle'
          ),
      tooltip: isProductionSet
        ? t(
            'productionsAndExports.createNewProduction.stepper.productionComponents.tooltip'
          )
        : t(
            'productionsAndExports.createNewProduction.stepper.components.tooltip'
          ),
      state: 'future',
    },
    {
      key: STEP_KEY_NUMBERING_AND_SORTING,
      label: t(
        'productionsAndExports.createNewProduction.stepper.numberingAndSorting.label'
      ),
      tooltipTitle: t(
        'productionsAndExports.createNewProduction.stepper.numberingAndSorting.tooltipTitle'
      ),
      tooltip: t(
        'productionsAndExports.createNewProduction.stepper.numberingAndSorting.tooltip'
      ),
      state: 'future',
    },
    {
      key: STEP_KEY_DOCUMENT_SELECTION,
      label: t(
        'productionsAndExports.createNewProduction.stepper.documentSelection.label'
      ),
      tooltip: t(
        'productionsAndExports.createNewProduction.stepper.documentSelection.tooltip'
      ),
      state: 'future',
    },
    {
      key: STEP_KEY_PRIV_GUARD,
      label: isProductionSet
        ? t('productionsAndExports.createNewProduction.stepper.privGuard.label')
        : t(
            'productionsAndExports.createNewProduction.stepper.exportGuard.label'
          ),
      tooltip: t(
        'productionsAndExports.createNewProduction.stepper.privGuard.tooltip'
      ),
      state: 'future',
    },
    {
      key: STEP_KEY_DELIVERY_OPTIONS,
      label: isProductionSet
        ? t(
            'productionsAndExports.createNewProduction.stepper.deliveryOptions.label'
          )
        : t(
            'productionsAndExports.createNewProduction.stepper.exportLocation.label'
          ),
      tooltipTitle: t(
        'productionsAndExports.createNewProduction.stepper.deliveryOptions.tooltipTitle'
      ),
      tooltip: t(
        'productionsAndExports.createNewProduction.stepper.deliveryOptions.tooltip'
      ),
      state: 'future',
    },
    {
      key: STEP_KEY_SUMMARY_AND_PREVIEW,
      label: t(
        'productionsAndExports.createNewProduction.stepper.summaryAndPreview.label'
      ),
      tooltip: t(
        'productionsAndExports.createNewProduction.stepper.summaryAndPreview.tooltip'
      ),
      state: 'future',
    },
    {
      key: STEP_KEY_GENERATE,
      label: t(
        'productionsAndExports.createNewProduction.stepper.generate.label'
      ),
      tooltipTitle: t(
        'productionsAndExports.createNewProduction.stepper.generate.tooltipTitle'
      ),
      tooltip: t(
        'productionsAndExports.createNewProduction.stepper.generate.tooltip'
      ),
      state: 'future',
    },
    {
      key: STEP_KEY_QUALITY_CONTROL_AND_CONFIRMATION,
      label: t(
        'productionsAndExports.createNewProduction.stepper.qualityControlAndConfirmation.label'
      ),
      tooltipTitle: t(
        'productionsAndExports.createNewProduction.stepper.qualityControlAndConfirmation.tooltipTitle'
      ),
      tooltip: t(
        'productionsAndExports.createNewProduction.stepper.qualityControlAndConfirmation.tooltip'
      ),
      state: 'future',
    },
  ] as Step[];
};

// Query parameter constants
export const QUERY_PARAM_PRODUCTION_ID = 'productionId';

// Route constants
export const ROUTE_PRODUCTION_HOME = '/Production/Home';
export const ROUTE_PRODUCTION_NEW = ROUTE_PRODUCTION_HOME + '/New';
export const ROUTE_PRODUCTION_EDIT = ROUTE_PRODUCTION_HOME + '/Edit';

// Helper function to map currentStep number from API to step key
export const getStepKeyFromNumber = (stepNumber: number): string => {
  const stepMap: {[key: number]: string} = {
    1: STEP_KEY_BASIC_INFO,
    2: STEP_KEY_PRODUCTION_COMPONENTS,
    3: STEP_KEY_NUMBERING_AND_SORTING,
    4: STEP_KEY_DOCUMENT_SELECTION,
    5: STEP_KEY_PRIV_GUARD,
    6: STEP_KEY_DELIVERY_OPTIONS,
    7: STEP_KEY_SUMMARY_AND_PREVIEW,
    8: STEP_KEY_GENERATE,
    9: STEP_KEY_QUALITY_CONTROL_AND_CONFIRMATION,
  };

  return stepMap[stepNumber] || STEP_KEY_BASIC_INFO; // Default to basicInfo if not found
};

// Static dropdown options for TIFF/PDF Component
export const getFontOptions = (): ISelectItem[] => [
  {value: 'Arial', label: 'Arial'},
  {value: 'Comic Sans MS', label: 'Comic Sans'},
  {value: 'Courier New', label: 'Courier New'},
  {value: 'Georgia', label: 'Georgia'},
  {value: 'Times', label: 'Times New Roman'},
  {value: 'Verdana', label: 'Verdana'},
];

export const getFontStyleOptions = (): ISelectItem[] => [
  {value: 'Regular', label: 'Regular'},
  {value: 'Italic', label: 'Italic'},
  {value: 'Bold', label: 'Bold'},
];

export const getFontSizeOptions = (): ISelectItem[] => [
  {value: '6', label: '6'},
  {value: '8', label: '8'},
  {value: '9', label: '9'},
  {value: '10', label: '10'},
  {value: '11', label: '11'},
  {value: '12', label: '12'},
  {value: '14', label: '14'},
  {value: '16', label: '16'},
];

export const getInsertFieldTypes = (): ISelectItem[] => [
  {
    value: 'metadata',
    label: t(
      'productionsAndExports.createNewProduction.common.toolbar.metadataField'
    ),
  },
  {
    value: 'production',
    label: t(
      'productionsAndExports.createNewProduction.common.toolbar.productionField'
    ),
  },
];

export const getPageOptions = (): ISelectItem[] => [
  {
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.generateTiff'
    ),
    value: 0,
  },
  {
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.generatePdf'
    ),
    value: 1,
  },
];

export const getPageTypes = (): ISelectItem[] => [
  {
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.multiPage'
    ),
    value: 1,
  },
  {
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.singlePage'
    ),
    value: 0,
  },
];

export const getPageSizes = (): ISelectItem[] => [
  {
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.letter'
    ),
    value: 0,
  },
  {
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.a4'
    ),
    value: 1,
  },
];

export const getRotationOptions = (): ISelectItem[] => [
  {
    value: '0',
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.rotation.options.noRotation'
    ),
  },
  {
    value: '-90',
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.rotation.options.rotateCounterClockwise90'
    ),
  },
  {
    value: '90',
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.rotation.options.rotateClockwise90'
    ),
  },
];

export const getPreserveColorOptions = (): ISelectItem[] => [
  {
    value: PreserveColorOption.SPECIFY_DOCS,
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.colorJpgOptions.specifyDocs'
    ),
  },
  {
    value: PreserveColorOption.ALL_DOCS,
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.colorJpgOptions.allDocs'
    ),
  },
];

export const getDocumentSelectionMethods = (): ISelectItem[] => [
  {
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.modals.selectDocuments.fileType'
    ),
    value: DocumentSelectionMethod.FILE_TYPE,
  },
  {
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.modals.selectDocuments.tags'
    ),
    value: DocumentSelectionMethod.TAGS,
  },
];

export const getRedactionTypeOptions = (): ISelectItem[] => [
  {
    value: 1,
    label: 'All Redactions in annotation layer: Default Annotation Layer',
  },
  {value: 0, label: 'Select a Redaction Tag'},
];

export const getLevelOptions = (): ISelectItem[] => [
  {
    label: t(
      'productionsAndExports.createNewProduction.numberingAndSorting.page'
    ),
    value: 0,
  },
  {
    label: t(
      'productionsAndExports.createNewProduction.numberingAndSorting.document'
    ),
    value: 1,
  },
];

export const getEncodingOptions = (): ISelectItem[] => [
  {
    label: t(
      'productionsAndExports.createNewProduction.numberingAndSorting.specifyBates'
    ),
    value: 1,
    info: t(
      'productionsAndExports.createNewProduction.numberingAndSorting.specifyBatesInfo'
    ),
  },
  {
    label: t(
      'productionsAndExports.createNewProduction.numberingAndSorting.useMetaData'
    ),
    value: 0,
    info: t(
      'productionsAndExports.createNewProduction.numberingAndSorting.useMetaDataInfo'
    ),
  },
];

export const getSortTypeOptions = (): ISelectItem[] => [
  {
    label: t(
      'productionsAndExports.createNewProduction.numberingAndSorting.sortingMetadata'
    ),
    value: 0,
    info: t(
      'productionsAndExports.createNewProduction.numberingAndSorting.sortByMetadataInfo'
    ),
    infoTitle: t(
      'productionsAndExports.createNewProduction.numberingAndSorting.sortByMetadataInfoTitle'
    ),
  },
  {
    label: t(
      'productionsAndExports.createNewProduction.numberingAndSorting.selectTags'
    ),
    value: 1,
    info: t(
      'productionsAndExports.createNewProduction.numberingAndSorting.selectTagsInfo'
    ),
    infoTitle: t(
      'productionsAndExports.createNewProduction.numberingAndSorting.selectTagsInfoTitle'
    ),
  },
  {
    label: t(
      'productionsAndExports.createNewProduction.numberingAndSorting.customSort'
    ),
    value: 2,
    info: t(
      'productionsAndExports.createNewProduction.numberingAndSorting.customSortInfo'
    ),
    infoTitle: t(
      'productionsAndExports.createNewProduction.numberingAndSorting.customSortInfoTitle'
    ),
  },
];

export const getKeepDocsOptions = (): ISelectItem[] => [
  {
    label: t(
      'productionsAndExports.createNewProduction.numberingAndSorting.docsBeginning'
    ),
    value: 1,
  },
  {
    label: t(
      'productionsAndExports.createNewProduction.numberingAndSorting.docsEnd'
    ),
    value: 0,
  },
];
